import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Img from "@insite/mobius/Img";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomState } from "@preferredMedical/Store/CustomState";
import { getProductPromotionsByProductId } from "@preferredMedical/Store/Data/ProductPromotions/ProductPromotionsSelectors";
import parse from "html-react-parser";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    [fields.backgroundColor]: string;
    [fields.textColor]: string;
    [fields.iconSrc]: string;
}

const enum fields {
    backgroundColor = "backgroundColor",
    textColor = "textColor",
    iconSrc = "iconSrc",
}

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const mapStateToProps = (state: CustomState, context: HasProduct) => {
    return {
        promotionLogo: getSettingsCollection(state).promotionLogo,
        productPromotions: getProductPromotionsByProductId(state, context.product.id),
        isLoggedIn:
            (state.context.session.isAuthenticated || state.context.session.rememberMe) &&
            !state.context.session.isGuest,
    };
};

type Props = WidgetProps & OwnProps & ReturnType<typeof mapStateToProps> & HasProduct;

export interface PromotionsMessageStyle {
    promo?: InjectableCss;
    cssWrapper?: InjectableCss;
}

export const promotionStyles: PromotionsMessageStyle = {
    promo: {
        css: css``,
    },
    cssWrapper: {
        css: css`
            display: flex;
            align-items: center;
            padding: 5px;
        `,
    },
};

const PromotionsMessage: React.FC<Props> = ({ product, promotionLogo, fields, productPromotions, isLoggedIn }): any => {
    if (!product || !product.productNumber || !isLoggedIn) {
        return null;
    }

    const extendedStyles = {
        promoBackground: {
            css: css`
                ${`background-color: ${fields.backgroundColor};`}
                ${`color: ${fields.textColor};`}
                margin-top: 10px;
                margin-bottom: 10px;
                border-radius: 5px;
                padding: 5px;
                span {
                    display: block;
                }
                span:first-child {
                    font-weight: bold;
                }
                img {
                    margin-right: 10px;
                }
            `,
        },
    };
    const [styles] = React.useState(() => mergeToNew(promotionStyles, extendedStyles));

    if (productPromotions.length === 0) {
        return null;
    }

    return (
        <>
            <StyledWrapper {...styles.promoBackground}>
                {productPromotions.map(promotion => (
                    <>
                        <StyledWrapper {...styles.cssWrapper}>
                            <Img width="40" height="40" src={promotionLogo as string} alt="logo" />
                            <div>
                                <Typography> {promotion.displayMessage}</Typography>
                                <Typography> {parse(promotion.description, parserOptions)}</Typography>
                            </div>
                        </StyledWrapper>
                    </>
                ))}
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(mapStateToProps)(PromotionsMessage)),
    definition: {
        displayName: "Product Promotions",
        group: "Product Details",
        fieldDefinitions: [
            {
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Background Color",
                defaultValue: "white",
                fieldType: "General",
                tab: basicTab,
                sortOrder: 1,
            },
            {
                name: fields.textColor,
                editorTemplate: "ColorPickerField",
                displayName: "Text Color",
                defaultValue: "black",
                fieldType: "General",
                tab: basicTab,
                sortOrder: 2,
            },
        ],
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
